import type {
  Action,
  ValidationResult,
  ValidationError,
} from "../types/actions";
import type { Project } from "../types";
import { TRACK_VOLUME_MAX, TRACK_VOLUME_MIN } from "../types/timeline";
import { getActionHandler } from "./registry";

export class ActionValidator {
  validate(action: Action, project: Project): ValidationResult {
    const errors: ValidationError[] = [];
    if (!action.type || typeof action.type !== "string") {
      errors.push({
        code: "INVALID_TYPE",
        message: "Action type is required and must be a string",
      });
      return { valid: false, errors };
    }

    if (!action.params || typeof action.params !== "object") {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Action params are required and must be an object",
      });
      return { valid: false, errors };
    }

    const handler = getActionHandler(action.type);
    if (handler) {
      return handler.validate(action, project);
    }

    // Every action type is a registered handler now, so anything reaching here
    // is a type nothing owns.
    return {
      valid: false,
      errors: [
        {
          code: "UNKNOWN_ACTION_TYPE",
          message: `Unknown action type: ${action.type}`,
        },
      ],
    };
  }
}

/**
 * Rules for track/volume params, shared by ActionValidator and by the
 * track/volume action handler so a value that is rejected in one path cannot
 * slip through the other.
 */
export function validateTrackVolumeParams(
  params: { trackId?: unknown; volume?: unknown },
  project: Project,
): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!params.trackId || typeof params.trackId !== "string") {
    errors.push({
      code: "INVALID_PARAMS",
      message: "Track ID is required and must be a string",
      path: "params.trackId",
    });
  } else if (
    !project.timeline.tracks.some((track) => track.id === params.trackId)
  ) {
    errors.push({
      code: "TRACK_NOT_FOUND",
      message: `Track with ID ${params.trackId} not found`,
      path: "params.trackId",
    });
  }

  if (
    typeof params.volume !== "number" ||
    !Number.isFinite(params.volume) ||
    params.volume < TRACK_VOLUME_MIN ||
    params.volume > TRACK_VOLUME_MAX
  ) {
    errors.push({
      code: "INVALID_PARAMS",
      message: `Volume must be a number between ${TRACK_VOLUME_MIN} and ${TRACK_VOLUME_MAX}`,
      path: "params.volume",
    });
  }

  return errors;
}
